import { api } from '../../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';

/**
 * THE THREE READS THE SAFETY BAND AND ITS PAGES SHARE, DEFINED ONCE.
 *
 * `SafetyLayout` draws the band and the section strip over the whole /safety
 * family, and it needs three figures that /safety's own panels and the Cert
 * Review queue also read. React Query keys the cache on `queryKey` alone, so
 * two components asking for `['safety-dashboard']` with DIFFERENT `queryFn`s
 * would each overwrite the other's answer with a differently-shaped one — and
 * whichever mounted last would win. One definition, imported by every reader,
 * is what keeps the band's figure and the panel's figure the same answer.
 *
 * These are moved verbatim from `SafetyScreen` (and, for the pending queue,
 * from `CertificationReviewQueue`, whose queryFn was already identical).
 */

export const safetyDashboardQuery = {
  queryKey: ['safety-dashboard'],
  queryFn: () => api.safety.dashboard().then((r: any) => r.data.data ?? r.data ?? {}),
  refetchOnMount: 'always' as const,
  ...LOAD_FAILURE_QUERY_OPTIONS,
};

export const teamCertSummaryQuery = {
  queryKey: ['team-cert-summary'],
  queryFn: () => api.safety.teamCertSummary().then((r: any) => r.data.data ?? r.data ?? {}),
  ...LOAD_FAILURE_QUERY_OPTIONS,
};

export const pendingCertsQuery = {
  queryKey: ['certifications-pending'],
  queryFn: () =>
    api.certifications.pending().then((r: any) => {
      const raw = r.data?.data?.certifications ?? r.data?.certifications ?? r.data?.data ?? r.data ?? [];
      return Array.isArray(raw) ? raw : [];
    }),
  ...LOAD_FAILURE_QUERY_OPTIONS,
};

/** The roles the review queue serves. The strip's Cert Review tab and its badge
 *  are shown to these and nobody else; the queue's route itself is not gated. */
export const CERT_REVIEWER_ROLES = ['admin', 'dispatcher', 'supervisor', 'office_staff'];
